/**
 * WhatsApi › Conversas (spec 2026-11-02 conversas §5.7): as DMs dos gateways da organização,
 * com o autor de cada mensagem; quem tem `whatsapi:atender` assume, responde pelo número do
 * gateway e devolve ao bot. Só no modo organização (o gateway é da organização).
 *
 * Três colunas: lista (aguardando humano primeiro), conversa e painel. Nas fatias 1+2 a tela
 * relê sozinha (lista a cada 5 s, conversa a cada 3 s, e ao focar a janela).
 * `?org=&gateway=&ref=` abre uma conversa direto (o "Abrir em Conversas" do gateway).
 */

import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Alert, Grid, Group, Loader, Paper, Select, Stack, Text } from '@mantine/core'

import type { ConversaWhatsApp } from '@/api/whatsapiOrg'
import { mensagemDeErro } from '@/components/admin/whatsappGateways/mensagens'
import { TituloDoEscopo } from '@/components/whatsapi/TituloDoEscopo'
import { ConversaAberta } from '@/components/whatsapi/conversas/ConversaAberta'
import { ListaDeConversas } from '@/components/whatsapi/conversas/ListaDeConversas'
import { PainelDaConversa } from '@/components/whatsapi/conversas/PainelDaConversa'
import { useConversas, useMensagensDaConversa } from '@/hooks/useConversasWhatsApi'
import { useEscopoWhatsApi } from '@/hooks/useEscopoWhatsApi'

const TODOS = ''

export function WhatsApiConversas() {
  const { escopo, opcoes, escolher, carregando } = useEscopoWhatsApi()
  const [params, setParams] = useSearchParams()
  const orgs = opcoes.organizacoes
  // A tela é da organização: quem também tem tenant cai na organização do escopo (ou na primeira).
  const org = orgs.find((o) => escopo.tipo === 'organizacao' && o.id === escopo.orgId) ?? orgs[0]
  const orgId = carregando ? undefined : org?.id

  // `?org=` do link: escolhe a organização na tela, sem mexer no seletor global.
  const orgDoLink = Number(params.get('org')) || null
  useEffect(() => {
    if (orgDoLink != null && orgs.some((o) => o.id === orgDoLink) && org?.id !== orgDoLink) {
      escolher('organizacao', orgDoLink)
    }
  }, [orgDoLink, orgs, org?.id, escolher])

  const filtroGw = params.get('filtro') ?? TODOS
  const aberta = params.get('gateway') && params.get('ref')
    ? { gwId: params.get('gateway') as string, ref: params.get('ref') as string }
    : null

  const lista = useConversas(orgId, filtroGw || null)
  const conversas = lista.itens
  const primeira = lista.cabeca
  const gateways = primeira?.gateways ?? []
  const conversa = aberta
    ? conversas.find((c) => c.gateway_id === aberta.gwId && c.ref === aberta.ref) ?? null
    : null

  const msgs = useMensagensDaConversa(orgId, aberta?.gwId ?? null, aberta?.ref ?? null)

  const mudar = (mudancas: Record<string, string | null>) => {
    const novo = new URLSearchParams(params)
    for (const [k, v] of Object.entries(mudancas)) {
      if (v) novo.set(k, v)
      else novo.delete(k)
    }
    setParams(novo, { replace: true })
  }
  const abrir = (c: ConversaWhatsApp) => mudar({ gateway: c.gateway_id, ref: c.ref })

  if (carregando) return <Loader size="sm" />
  if (!org || orgId == null) {
    return (
      <Alert color="gray" variant="light" title="Conversas">
        Conversas é da organização dona do gateway WhatsApp, e você não tem acesso ao WhatsApi de nenhuma organização.
      </Alert>
    )
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <TituloDoEscopo titulo="Conversas" escopo={{ tipo: 'organizacao', orgId: org.id, pode: org.pode }} nome={org.name} />
        <Group gap="xs">
          {orgs.length > 1 && (
            <Select size="sm" aria-label="Organização" w={220} allowDeselect={false}
              value={String(org.id)}
              onChange={(v) => {
                if (!v) return
                escolher('organizacao', Number(v))
                mudar({ org: v, gateway: null, ref: null, filtro: null })
              }}
              data={orgs.map((o) => ({ value: String(o.id), label: o.name }))} />
          )}
          {gateways.length > 1 && (
            <Select size="sm" aria-label="Gateway" w={220} allowDeselect={false}
              value={filtroGw}
              onChange={(v) => mudar({ filtro: v || null })}
              data={[{ value: TODOS, label: 'Todos os gateways' }, ...gateways.map((g) => ({ value: g.id, label: g.nome }))]} />
          )}
        </Group>
      </Group>

      {!!lista.erro && <Alert color="red" variant="light">{mensagemDeErro(lista.erro)}</Alert>}
      {primeira && !primeira.disponivel && (
        <Alert color="gray" variant="light">Indisponível nesta versão do WhatsApi.</Alert>
      )}
      {primeira?.disponivel && gateways.length === 0 && (
        <Alert color="gray" variant="light">Esta organização ainda não tem gateway WhatsApp pronto.</Alert>
      )}

      <Grid gutter="md" align="flex-start">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper withBorder p="sm" radius="md">
            {lista.carregando ? <Loader size="sm" /> : (
              <ListaDeConversas conversas={conversas} selecionada={aberta} aoEscolher={abrir}
                comGateway={gateways.length > 1} temMais={lista.temMais}
                carregandoMais={lista.carregandoMais} aoCarregarMais={lista.carregarMais} />
            )}
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper withBorder p="sm" radius="md">
            {aberta ? (
              <ConversaAberta key={`${aberta.gwId}/${aberta.ref}`} mensagens={msgs.itens} carregando={msgs.carregando} erro={msgs.erro}
                historicoIncompleto={[msgs.cabeca, ...msgs.paginasAnteriores].some((p) => p?.historico_incompleto)}
                temAnteriores={msgs.temMais} carregandoAnteriores={msgs.carregandoMais}
                aoCarregarAnteriores={msgs.carregarMais} />
            ) : (
              <Text size="sm" c="dimmed">Escolha uma conversa na lista.</Text>
            )}
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 3 }}>
          <Paper withBorder p="sm" radius="md">
            {aberta ? (
              <PainelDaConversa key={`${aberta.gwId}/${aberta.ref}`} orgId={org.id} gwId={aberta.gwId}
                conversaRef={aberta.ref} conversa={conversa} podeAtender={org.pode.atender} />
            ) : (
              <Text size="sm" c="dimmed">Nenhuma conversa aberta.</Text>
            )}
          </Paper>
        </Grid.Col>
      </Grid>
    </Stack>
  )
}
